import React, { useState } from 'react';
import { Activity, Award, Dog, Download, PawPrint, Plus, QrCode, Search, Share2, X } from 'lucide-react';

// Vistas de la pestaña Manada y Genealogía (antes vivían dentro de BreederDashboard.tsx).
// Paleta: naranja de marca (#FF6B00) para acciones e interacción, cielo para "padre",
// rosa para "madre", ámbar para lo relativo a certificación/pedigrí — la misma familia
// de colores que la portada pública de /criaderos. Nada de paneles oscuros llenos.

export const ParentSelectionModal = ({
    isOpen,
    onClose,
    onSelect,
    candidates,
    title,
    type
}: {
    isOpen: boolean,
    onClose: () => void,
    onSelect: (id: any) => void,
    candidates: any[],
    title: string,
    type: 'padre' | 'madre'
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredCandidates = candidates.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.microchip && c.microchip.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="bg-white rounded-[2.5rem] p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-xl font-black text-slate-800 uppercase italic">Seleccionar {title}</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Buscando en la manada</p>
                    </div>
                    <button onClick={onClose} className="bg-slate-50 p-2 rounded-[7px] text-slate-400 hover:text-slate-600 transition-colors"><X size={20} /></button>
                </div>

                <div className="relative mb-4">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                        type="text"
                        placeholder="Buscar por nombre o chip..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 pl-11 pr-4 py-3 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold text-xs"
                        autoFocus
                    />
                </div>

                <div className="flex-1 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                    <button
                        onClick={() => { onSelect('external'); onClose(); }}
                        className="w-full p-4 rounded-[7px] border-2 border-dashed border-slate-200 text-slate-400 hover:border-orange-400 hover:text-orange-500 font-black uppercase text-[10px] tracking-widest transition-all mb-4"
                    >
                        {title} Externo (No registrado)
                    </button>

                    {filteredCandidates.map(candidate => (
                        <button
                            key={candidate.id}
                            onClick={() => { onSelect(candidate.id); onClose(); }}
                            className="w-full p-3 rounded-[7px] bg-slate-50 hover:bg-slate-100 border border-slate-100 flex items-center gap-3 transition-colors text-left"
                        >
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-white shadow-sm shrink-0">
                                <img src={candidate.imageUrl} className="w-full h-full object-cover" />
                            </div>
                            <div>
                                <p className="text-xs font-black text-slate-800 uppercase italic">{candidate.name}</p>
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{candidate.breed}</p>
                            </div>
                            <div className={`ml-auto px-2 py-1 rounded-[7px] text-[8px] font-black uppercase ${type === 'padre' ? 'bg-sky-100 text-sky-600' : 'bg-rose-100 text-rose-600'
                                }`}>
                                {type.toUpperCase()}
                            </div>
                        </button>
                    ))}

                    {filteredCandidates.length === 0 && (
                        <p className="text-center text-xs text-slate-400 py-4 italic">No se encontraron resultados</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export const ManadaView = ({ pets, onSelectQrPet, onTransfer, onAdoption, onAddNew, onGoToPedigree, onViewDetail }: {
    pets: any[],
    onSelectQrPet: (pet: any) => void,
    onTransfer: (pet: any) => void,
    onAdoption: (pet: any) => void,
    onAddNew: () => void,
    onGoToPedigree: (pet: any) => void,
    onViewDetail: (pet: any) => void
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'todos' | 'criadero' | 'disponible' | 'reservado' | 'entregado'>('todos');

    const term = searchTerm.toLowerCase();
    const filteredPets = pets.filter(pet =>
        (statusFilter === 'todos' || pet.status === statusFilter) && (
            String(pet.name || '').toLowerCase().includes(term) ||
            String(pet.breed || '').toLowerCase().includes(term) ||
            String(pet.microchip || '').toLowerCase().includes(term)
        )
    );
    // Exporta la manada (lo que muestran los filtros) a un CSV que abre Excel: nombre, raza, estado, chip, pedigrí…
    const exportCsv = () => {
        const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const head = ['Nombre', 'Raza', 'Sexo', 'Nacimiento', 'Estado', 'Microchip', 'Pedigrí N°', 'Pedigrí verificado', 'Precio'];
        const rows = filteredPets.map(p => [p.name, p.breed, p.gender, p.birthDate ? String(p.birthDate).slice(0, 10) : '', p.status, p.microchip, p.pedigreeReg, p.pedigreeVerified ? 'Sí' : 'No', p.price ?? '']);
        const csv = '﻿' + [head, ...rows].map(r => r.map(esc).join(';')).join('\r\n');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        a.download = `manada_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
    };
    const count = (s: string) => pets.filter(p => p.status === s).length;
    const FILTERS: { id: typeof statusFilter; label: string; n: number }[] = [
        { id: 'todos', label: 'Todos', n: pets.length },
        { id: 'criadero', label: 'Reproductores', n: count('criadero') },
        { id: 'disponible', label: 'Disponibles', n: count('disponible') },
        { id: 'reservado', label: 'Reservados', n: count('reservado') },
        { id: 'entregado', label: 'Entregados', n: count('entregado') },
    ];

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
                {FILTERS.map(f => (
                    <button key={f.id} onClick={() => setStatusFilter(f.id)}
                        className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${statusFilter === f.id ? 'bg-[#FF6B00] text-white shadow-lg' : 'bg-white text-slate-500 border border-slate-200 hover:border-orange-300'}`}>
                        {f.label} <span className={statusFilter === f.id ? 'text-orange-200' : 'text-slate-300'}>{f.n}</span>
                    </button>
                ))}
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                    <h2 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter">Mi Manada</h2>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Ejemplares registrados ({filteredPets.length})</p>
                </div>

                <div className="flex items-center gap-4 w-full sm:w-auto">
                    <div className="relative flex-1 sm:w-64">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                        <input
                            type="text"
                            placeholder="Buscar por nombre, raza o chip..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-white border border-slate-200 pl-11 pr-4 py-3 rounded-[7px] outline-none focus:border-[#FF6B00] focus:ring-4 focus:ring-orange-500/5 transition-all font-bold text-xs"
                        />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500">
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    <button onClick={exportCsv} disabled={filteredPets.length === 0} className="bg-white border border-slate-200 text-slate-600 px-4 py-3 rounded-[7px] font-black uppercase text-[10px] flex items-center gap-2 hover:border-orange-300 hover:text-orange-600 transition-colors shrink-0 disabled:opacity-40" title="Descargar la lista en Excel (CSV)"><Download size={14} /> <span className="hidden sm:inline">Exportar</span></button>
                    <button
                        onClick={onAddNew}
                        className="bg-[#FF6B00] text-white px-6 py-3 rounded-[7px] font-black uppercase text-[10px] flex items-center gap-2 shadow-lg shadow-orange-600/20 hover:scale-105 transition-transform shrink-0"
                    >
                        <Plus size={16} /> <span className="hidden xs:inline">Registrar Miembro</span><span className="xs:hidden">Nuevo</span>
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPets.map(pet => (
                    <div key={pet.id} onClick={() => onViewDetail(pet)} className="bg-white rounded-[2rem] p-6 shadow-xl border border-slate-100 flex flex-col items-center group hover:border-[#FF6B00] transition-all relative cursor-pointer">
                        {/* Status Badge */}
                        <div className={`absolute top-6 right-6 px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest shadow-sm ${pet.status === 'disponible' ? 'bg-emerald-100 text-emerald-600' :
                            pet.status === 'reservado' ? 'bg-amber-100 text-amber-600' :
                                pet.status === 'entregado' ? 'bg-slate-100 text-slate-500' :
                                    'bg-sky-100 text-sky-600'
                            }`}>
                            {pet.status === 'criadero' ? 'Reproductor' : (pet.status || 'Criadero')}
                        </div>

                        <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg overflow-hidden mb-4 bg-slate-100 flex items-center justify-center">
                            {pet.imageUrl
                                ? <img src={pet.imageUrl} className="w-full h-full object-cover" />
                                : <Dog size={48} className="text-slate-300" />}
                        </div>

                        <h3 className="font-black text-slate-800 text-lg uppercase italic">{pet.name}</h3>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{pet.breed || 'Sin raza'} • {pet.gender}</p>
                        <p className="text-[10px] font-black text-[#FF6B00] uppercase tracking-widest mb-4 flex items-center gap-1 cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); onGoToPedigree(pet); }}>
                            <Activity size={10} /> Ver Genealogía
                        </p>

                        <div className="flex gap-2 w-full" onClick={e => e.stopPropagation()}>
                            <button
                                onClick={() => onSelectQrPet(pet)}
                                className="flex-1 bg-slate-900 text-white rounded-[7px] py-2 text-[10px] font-black uppercase flex items-center justify-center gap-2 hover:bg-black transition-colors">
                                <QrCode size={14} /> Laser QR
                            </button>
                            {pet.status === 'entregado' ? (
                                <p className="flex-1 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest py-2">Ya tiene nuevo dueño</p>
                            ) : (
                                <button
                                    onClick={() => onTransfer(pet)}
                                    className="flex-1 bg-[#FF6B00] text-white rounded-[7px] py-2 text-[10px] font-black uppercase flex items-center justify-center gap-2 hover:bg-[#E65C00] transition-colors"
                                    title="Entregar el ejemplar a su nuevo dueño"
                                >
                                    <Share2 size={14} /> Entregar
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                {filteredPets.length === 0 && (
                    <div className="col-span-full py-20 text-center text-slate-400 bg-slate-50 rounded-[3rem] border-2 border-dashed border-slate-200">
                        <Search size={48} className="mx-auto mb-4 opacity-50" />
                        <p className="font-bold uppercase tracking-widest text-xs">No se encontraron mascotas {searchTerm ? 'con ese criterio' : 'en la manada'}.</p>
                        {searchTerm ? (
                            <button onClick={() => setSearchTerm('')} className="mt-4 text-[#FF6B00] font-black uppercase text-[10px] underline">Limpiar búsqueda</button>
                        ) : (
                            <button onClick={onAddNew} className="mt-4 text-[#FF6B00] font-black uppercase text-[10px] underline">Comienza aquí</button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// Familia de color por línea de sangre: paterna = cielo, materna = rosa. Se usa tanto en el
// fondo agrupador de abuelos como en cada tarjeta individual, para que la vista se lea de un
// vistazo sin tener que fijarse en las etiquetas.
const LINE = {
    father: { text: 'text-sky-600', dim: 'text-sky-400', chip: 'bg-sky-500', border: 'border-sky-200', tint: 'bg-sky-50', group: 'bg-sky-50/70 border-sky-100' },
    mother: { text: 'text-rose-600', dim: 'text-rose-400', chip: 'bg-rose-500', border: 'border-rose-200', tint: 'bg-rose-50', group: 'bg-rose-50/70 border-rose-100' },
} as const;

const PedigreeNode: React.FC<{ pet: any; label: string; side: 'father' | 'mother'; gen: string; size?: 'sm' | 'lg' }> = ({ pet, label, side, gen, size = 'sm' }) => {
    const c = LINE[side];
    const lg = size === 'lg';
    return (
        <div className={`flex items-center gap-2.5 ${lg ? 'p-3.5' : 'p-2'} rounded-[7px] border-2 bg-white shadow-sm hover:shadow-md transition-all min-w-0 ${pet ? c.border : 'border-dashed border-slate-200'}`}>
            <div className="relative shrink-0">
                <div className={`${lg ? 'w-12 h-12' : 'w-8 h-8'} rounded-[7px] overflow-hidden border-2 border-white shadow ${pet ? c.tint : 'bg-slate-50'} flex items-center justify-center`}>
                    {pet?.imageUrl ? <img src={pet.imageUrl} className="w-full h-full object-cover" /> : <Plus size={lg ? 14 : 10} className="text-slate-300" />}
                </div>
                <div className={`absolute -bottom-1 -right-1 ${lg ? 'w-4.5 h-4.5 text-[7px]' : 'w-3.5 h-3.5 text-[6px]'} rounded-[7px] flex items-center justify-center text-white font-black shadow ${pet ? c.chip : 'bg-slate-300'}`}>{gen}</div>
            </div>
            <div className="flex flex-col min-w-0">
                <p className={`${lg ? 'text-[8px]' : 'text-[7px]'} font-black uppercase tracking-widest leading-none mb-1 truncate ${pet ? c.dim : 'text-slate-300'}`}>{label}</p>
                {pet ? (
                    <>
                        <span className={`${lg ? 'text-[12px]' : 'text-[10px]'} font-black uppercase text-slate-800 italic tracking-tighter leading-none mb-0.5 truncate`}>{pet.name}</span>
                        <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest truncate">{pet.breed || 'Sin raza'}</span>
                    </>
                ) : <span className="text-[9px] font-bold text-slate-300 italic">Desconocido</span>}
            </div>
        </div>
    );
};

export const PedigreeView = ({ selectedPet, inventory, onSelectPet }: { selectedPet: any, inventory: any[], onSelectPet: (pet: any) => void }) => {
    if (!selectedPet) {
        return (
            <div className="bg-white rounded-[2rem] p-8 shadow-xl border border-slate-100 text-center min-h-[400px] flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-orange-50 rounded-[1.6rem] flex items-center justify-center mb-5"><Activity size={28} className="text-[#FF6B00]" /></div>
                <h3 className="text-xl font-black text-slate-800 uppercase italic">Árbol Genealógico</h3>
                <p className="text-slate-500 text-sm mt-2 max-w-md">Selecciona un ejemplar de tu manada para visualizar su linaje profesional.</p>
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4 w-full max-w-2xl">
                    {inventory.map(pet => (
                        <button key={pet.id} onClick={() => onSelectPet(pet)} className="p-4 bg-slate-50 hover:bg-orange-50 rounded-[7px] transition-all flex flex-col items-center border border-slate-100 hover:border-orange-200">
                            <div className="w-12 h-12 rounded-full overflow-hidden mb-2 border-2 border-white shadow-sm bg-slate-100 flex items-center justify-center">
                                {pet.imageUrl ? <img src={pet.imageUrl} className="w-full h-full object-cover" /> : <Dog size={18} className="text-slate-300" />}
                            </div>
                            <span className="text-[10px] font-black uppercase text-slate-600 truncate w-full text-center">{pet.name}</span>
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    const getParent = (id: any) => inventory.find(p => p.id == id);

    const father = getParent(selectedPet.fatherId);
    const mother = getParent(selectedPet.motherId);

    // Grandparents
    const gf1 = father ? getParent(father.fatherId) : null;
    const gm1 = father ? getParent(father.motherId) : null;
    const gf2 = mother ? getParent(mother.fatherId) : null;
    const gm2 = mother ? getParent(mother.motherId) : null;

    // Descendencia: hijos (ejemplares que tienen a selectedPet como padre o madre)
    // y nietos (hijos de esos hijos), para que el árbol también muestre hacia abajo.
    const children = inventory.filter(p => p.id != selectedPet.id && (p.fatherId == selectedPet.id || p.motherId == selectedPet.id));
    const grandchildren = children.flatMap(c => inventory.filter(p => p.id != c.id && (p.fatherId == c.id || p.motherId == c.id)));

    return (
        <div className="bg-white rounded-[3rem] p-6 sm:p-10 lg:p-14 shadow-xl border border-orange-100 relative overflow-hidden">
            {/* marca de agua sutil, mismo recurso que usa la portada pública */}
            <PawPrint size={240} className="absolute -top-10 -right-10 text-orange-50 rotate-12 pointer-events-none" aria-hidden="true" />

            <div className="flex flex-wrap justify-between items-center gap-4 mb-10 sm:mb-14 relative z-10">
                <div className="flex items-center gap-4 min-w-0">
                    <button onClick={() => onSelectPet(null)} className="p-2.5 bg-slate-100 hover:bg-slate-200 rounded-[7px] text-slate-500 transition-colors shrink-0"><X size={16} /></button>
                    <div className="min-w-0">
                        <p className="text-[9px] font-black text-orange-500 uppercase tracking-[0.2em]">Árbol genealógico</p>
                        <h3 className="text-xl sm:text-2xl font-black text-slate-800 uppercase italic tracking-tighter truncate">{selectedPet.name}</h3>
                    </div>
                </div>
                <div className="px-4 py-2 bg-gradient-to-r from-amber-400 to-amber-500 rounded-full text-[10px] font-black uppercase text-white shadow-lg shadow-amber-500/25 flex items-center gap-1.5 shrink-0">
                    <Award size={13} /> Certificado Digital
                </div>
            </div>

            <div className="flex flex-col items-center gap-5 sm:gap-7 relative z-10">
                {/* Generación 3 — agrupada por línea paterna / materna */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 w-full">
                    <div className={`rounded-[1.6rem] border p-3 sm:p-4 ${LINE.father.group}`}>
                        <p className={`text-[8px] font-black uppercase tracking-widest mb-2.5 text-center ${LINE.father.text}`}>Línea paterna</p>
                        <div className="grid grid-cols-2 gap-2">
                            <PedigreeNode pet={gf1} label="Abuelo" side="father" gen="G3" />
                            <PedigreeNode pet={gm1} label="Abuela" side="father" gen="G3" />
                        </div>
                    </div>
                    <div className={`rounded-[1.6rem] border p-3 sm:p-4 ${LINE.mother.group}`}>
                        <p className={`text-[8px] font-black uppercase tracking-widest mb-2.5 text-center ${LINE.mother.text}`}>Línea materna</p>
                        <div className="grid grid-cols-2 gap-2">
                            <PedigreeNode pet={gf2} label="Abuelo" side="mother" gen="G3" />
                            <PedigreeNode pet={gm2} label="Abuela" side="mother" gen="G3" />
                        </div>
                    </div>
                </div>

                <div className="w-px h-8 bg-orange-100" aria-hidden="true" />

                {/* Generación 2 — padres */}
                <div className="grid grid-cols-2 gap-3 sm:gap-6 w-full max-w-lg">
                    <PedigreeNode pet={father} label="Padre (Sire)" side="father" gen="G2" size="lg" />
                    <PedigreeNode pet={mother} label="Madre (Dam)" side="mother" gen="G2" size="lg" />
                </div>

                <div className="w-px h-10 bg-orange-200" aria-hidden="true" />

                {/* Generación 1 — el ejemplar */}
                <div className="p-6 rounded-[2.5rem] bg-gradient-to-br from-orange-50 via-white to-white border-2 border-[#FF6B00] shadow-lg flex flex-col items-center min-w-[200px]">
                    <div className="w-20 h-20 rounded-full border-4 border-white shadow-lg overflow-hidden mb-3 bg-slate-100 flex items-center justify-center">
                        {selectedPet.imageUrl ? <img src={selectedPet.imageUrl} className="w-full h-full object-cover" /> : <Dog size={28} className="text-slate-300" />}
                    </div>
                    <h4 className="text-lg font-black text-slate-800 uppercase italic tracking-tighter">{selectedPet.name}</h4>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{selectedPet.breed || 'Sin raza'}</p>
                </div>

                {/* Descendencia — hijos y nietos, si los tiene */}
                {children.length > 0 && (
                    <>
                        <div className="w-px h-10 bg-orange-200" aria-hidden="true" />
                        <div className="w-full">
                            <p className="text-[8px] font-black uppercase tracking-widest mb-2.5 text-center text-slate-400">Hijos</p>
                            <div className="flex flex-wrap justify-center gap-2.5">
                                {children.map(c => (
                                    <PedigreeNode key={c.id} pet={c} label={['Macho', 'Male', 'M', 'm'].includes(c.gender) ? 'Hijo' : 'Hija'}
                                        side={['Macho', 'Male', 'M', 'm'].includes(c.gender) ? 'father' : 'mother'} gen="G1↓" size="lg" />
                                ))}
                            </div>
                        </div>
                    </>
                )}
                {grandchildren.length > 0 && (
                    <>
                        <div className="w-px h-8 bg-orange-100" aria-hidden="true" />
                        <div className="w-full">
                            <p className="text-[8px] font-black uppercase tracking-widest mb-2.5 text-center text-slate-400">Nietos</p>
                            <div className="flex flex-wrap justify-center gap-2">
                                {grandchildren.map(g => (
                                    <PedigreeNode key={g.id} pet={g} label={['Macho', 'Male', 'M', 'm'].includes(g.gender) ? 'Nieto' : 'Nieta'}
                                        side={['Macho', 'Male', 'M', 'm'].includes(g.gender) ? 'father' : 'mother'} gen="G2↓" />
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
